/**
 * Registering — and un-registering — the service worker (LV §127-132, §181).
 *
 * The registration used to be injected into `index.html` by `vite-plugin-pwa` at
 * build time, which put it beyond the reach of any decision: the worker installed
 * itself on load, before the application had asked the server what this installation
 * offers. `FEATURE_OFFLINE=false` could not stop it, because nothing in the running
 * application was ever consulted.
 *
 * So `vite.config.ts` now sets `injectRegister: null` and the registration happens
 * here, after `GET /configuration/feature-flags` has answered (LV §134 — the client
 * hides what the server said to hide). Two directions, both needed:
 *
 * * `registerServiceWorker()` — offline is on. Installs the worker that serves the
 *   shell and the LV §127 read endpoints without a network.
 * * `unregisterServiceWorker()` — offline is off. A device that was running with the
 *   flag on still has a worker and a filled cache; leaving it installed would keep
 *   serving an offline-capable shell in an installation that has switched offline
 *   operation off, which is the same defect from the other side.
 *
 * The URL is a literal rather than `virtual:pwa-register`: the plugin builds
 * `src/offline/service-worker.ts` to `/service-worker.js` (its `resolveSwPaths`
 * renames a `.ts` source to `.js` and serves it from the build base), and importing
 * the virtual module would drag a build-time-only specifier into modules that the
 * unit tests load.
 */

/** Where `vite-plugin-pwa` writes the built worker (`injectManifest`, `.ts` source). */
export const SERVICE_WORKER_URL = "/service-worker.js";

/** Cache buckets the worker owns. Named in `service-worker.ts`; dropped with it. */
const CACHE_PREFIX = "dah-";

export function serviceWorkerSupported(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

/**
 * Install the worker. Returns whether one is now registered.
 *
 * Only in a production build: `devOptions.enabled` is false in `vite.config.ts`, so
 * the dev server does not serve `/service-worker.js` and a registration there would
 * be a 404 in the console on every reload.
 */
export async function registerServiceWorker(): Promise<boolean> {
  if (!serviceWorkerSupported() || !import.meta.env.PROD) return false;
  try {
    await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: "/" });
    return true;
  } catch (error) {
    // A failed registration costs offline reading, not correctness — every mutation
    // still goes to the server. Logged rather than shown: there is nothing the user
    // could do about it, and an error banner on sign-in would be alarming and useless.
    console.warn("service_worker_registration_failed", error);
    return false;
  }
}

/**
 * Remove the worker and everything it cached. Returns whether anything was removed.
 *
 * The caches go too. A worker that is unregistered but whose caches survive would
 * leave a stale copy of the app shell and of the LV §127 read endpoints on the
 * device — reachable again the moment offline is switched back on, and by then
 * potentially a release or two out of date.
 *
 * The IndexedDB queue is deliberately *not* touched. It may hold work a technician
 * captured before the flag changed, and Arch §15's "conflicts must never silently
 * destroy data" applies at least as strongly to a configuration change: the records
 * stay, and stay visible.
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!serviceWorkerSupported()) return false;

  let removed = false;
  try {
    for (const registration of await navigator.serviceWorker.getRegistrations()) {
      removed = (await registration.unregister()) || removed;
    }
  } catch (error) {
    console.warn("service_worker_unregistration_failed", error);
  }

  try {
    if (typeof caches !== "undefined") {
      for (const name of await caches.keys()) {
        if (name.startsWith(CACHE_PREFIX)) await caches.delete(name);
      }
    }
  } catch (error) {
    console.warn("service_worker_cache_cleanup_failed", error);
  }

  return removed;
}
